import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    UploadSimple,
    DownloadSimple,
    Prohibit,
    Trophy,
    MapPin,
    Trash,
    FileText,
    FilePdf,
    Warning,
} from "@phosphor-icons/react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { AdsImportBar } from "@/components/AdsImportBar";
import { adsGeographic, adsAuditGet } from "@/lib/api";

/* ------------------------------------------------------------------------- */
/* CSV parsing (shared logic — kept local so this file remains standalone)    */
/* ------------------------------------------------------------------------- */

const stripBom = (s) => s.replace(/^\uFEFF/, "");

const parseCsv = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    const s = stripBom(text);
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (inQuotes) {
            if (ch === '"' && s[i + 1] === '"') {
                field += '"';
                i++;
            } else if (ch === '"') {
                inQuotes = false;
            } else {
                field += ch;
            }
        } else {
            if (ch === '"') inQuotes = true;
            else if (ch === ",") {
                cur.push(field);
                field = "";
            } else if (ch === "\r") {
                /* skip */
            } else if (ch === "\n") {
                cur.push(field);
                rows.push(cur);
                cur = [];
                field = "";
            } else field += ch;
        }
    }
    if (field.length > 0 || cur.length > 0) {
        cur.push(field);
        rows.push(cur);
    }
    return rows;
};

const toCsv = (rows) =>
    rows
        .map((row) =>
            row
                .map((v) => {
                    const s = String(v ?? "");
                    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
                    return s;
                })
                .join(",")
        )
        .join("\n");

const downloadCsv = (filename, content) => {
    const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const normalizeHeader = (h) =>
    String(h || "")
        .replace(/\uFEFF/g, "")
        // Google Ads exports occasionally break long headers across lines like "Conversio ns" or "Added/Excl uded".
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

const parseNumber = (raw) => {
    if (raw === null || raw === undefined) return 0;
    let s = String(raw).trim();
    if (!s || s === "--" || s === "—") return 0;
    s = s.replace(/[£$€₹A-Za-z\s]/g, "");
    if (s.includes(",") && s.includes(".")) s = s.replace(/,/g, "");
    else if (s.includes(",") && !s.includes(".")) s = s.replace(/\./g, "").replace(",", ".");
    s = s.replace(/%$/, "");
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};

/* ------------------------------------------------------------------------- */
/* Location report header aliases                                             */
/* ------------------------------------------------------------------------- */

// Note: Google sometimes exports "Conversio ns" (with a stray space). normalizeHeader collapses it to "conversio ns" and we handle that variant.
const HEADER_ALIASES = {
    location: [
        "matched location",
        "location",
        "target location",
        "user location",
        "city",
        "region",
        "metro",
        "ubicación coincidente",
        "ubicación",
        "emplacement",
    ],
    clicks: ["clicks", "clics", "klicks"],
    impressions: ["impr.", "impressions", "impresiones", "impressionen"],
    ctr: ["ctr", "click-through rate"],
    currency: ["currency code", "currency"],
    avg_cpc: ["avg. cpc", "avg cpc", "average cpc"],
    cost: ["cost", "spend", "coste", "coût", "kosten"],
    conversions: ["conversions", "conv.", "conv", "conversio ns", "conversiones"],
    conv_rate: ["conv. rate", "conv rate", "conversion rate"],
    cost_per_conv: [
        "cost / conv.",
        "cost/conv.",
        "cost per conv.",
        "cost per conversion",
        "cpa",
    ],
};

const findHeaderIndex = (headers, key) => {
    const normalized = headers.map(normalizeHeader);
    const aliases = HEADER_ALIASES[key] || [];
    for (const a of aliases) {
        const idx = normalized.indexOf(a.toLowerCase());
        if (idx !== -1) return idx;
    }
    if (normalized.length < 3) return -1;
    for (let i = 0; i < normalized.length; i++) {
        for (const a of aliases) {
            if (
                normalized[i].startsWith(a.toLowerCase() + " ") ||
                normalized[i] === a.toLowerCase()
            )
                return i;
        }
    }
    return -1;
};

const isTotalRow = (locStr) => {
    const s = normalizeHeader(locStr);
    return s.startsWith("total") || s.includes("total:");
};

/* ------------------------------------------------------------------------- */
/* Component                                                                  */
/* ------------------------------------------------------------------------- */

export const LocationAuditor = ({ preloadedAudit, onAuditSaved }) => {
    const [rawRows, setRawRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [filename, setFilename] = useState("");
    const [wasteMinCost, setWasteMinCost] = useState(50); // only flag waste locations spending ≥ £50
    const [selectedNeg, setSelectedNeg] = useState({});
    const fileInputRef = useRef(null);

    const handleFile = (file) => {
        if (!file) return;
        const lowerName = (file.name || "").toLowerCase();
        if (!lowerName.endsWith(".csv") && !file.type.includes("csv")) {
            toast.error(
                `That looks like a ${lowerName.split(".").pop() || "non-CSV"} file. Please export your Locations report as CSV from Google Ads (Reports → Download → CSV).`,
                { duration: 8000 }
            );
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target.result;
                const grid = parseCsv(text).filter((r) => r.some((c) => c && c.trim()));
                if (!grid.length) {
                    toast.error("CSV appears empty");
                    return;
                }
                // Locate the header row — the row whose first cells include a location column.
                let headerIdx = 0;
                for (let i = 0; i < Math.min(grid.length, 6); i++) {
                    if (findHeaderIndex(grid[i], "location") !== -1) {
                        headerIdx = i;
                        break;
                    }
                }
                const hdrs = grid[headerIdx];
                const body = grid.slice(headerIdx + 1);
                if (findHeaderIndex(hdrs, "location") === -1) {
                    toast.error(
                        "No 'Matched location' / City column found — check the CSV format."
                    );
                    return;
                }
                setHeaders(hdrs);
                setRawRows(body);
                setFilename(file.name);
                setSelectedNeg({});
                toast.success(`Loaded ${body.length} rows from ${file.name}`);
            } catch (e) {
                toast.error("Failed to parse CSV: " + (e.message || "unknown"));
            }
        };
        reader.onerror = () => toast.error("Could not read file");
        reader.readAsText(file);
    };

    const normalized = useMemo(() => {
        if (!rawRows.length || !headers.length) return [];
        const locIdx = findHeaderIndex(headers, "location");
        const clkIdx = findHeaderIndex(headers, "clicks");
        const impIdx = findHeaderIndex(headers, "impressions");
        const ctrIdx = findHeaderIndex(headers, "ctr");
        const costIdx = findHeaderIndex(headers, "cost");
        const convIdx = findHeaderIndex(headers, "conversions");
        const cpaIdx = findHeaderIndex(headers, "cost_per_conv");
        const convRateIdx = findHeaderIndex(headers, "conv_rate");

        return rawRows
            .map((r) => {
                const location = (r[locIdx] || "").trim();
                if (!location || isTotalRow(location)) return null;
                const clicks = parseNumber(r[clkIdx]);
                const impressions = parseNumber(r[impIdx]);
                const cost = parseNumber(r[costIdx]);
                const conversions = parseNumber(r[convIdx]);
                const rawCtr =
                    ctrIdx !== -1
                        ? parseNumber(r[ctrIdx])
                        : impressions > 0
                        ? (clicks / impressions) * 100
                        : 0;
                const cpaFromCsv = cpaIdx !== -1 ? parseNumber(r[cpaIdx]) : 0;
                const cost_per_conv =
                    cpaFromCsv > 0
                        ? cpaFromCsv
                        : conversions > 0
                        ? cost / conversions
                        : null;
                const conv_rate =
                    convRateIdx !== -1
                        ? parseNumber(r[convRateIdx])
                        : clicks > 0
                        ? (conversions / clicks) * 100
                        : 0;
                return {
                    location,
                    impressions,
                    clicks,
                    ctr: rawCtr,
                    cost,
                    conversions,
                    conv_rate,
                    cost_per_conv,
                };
            })
            .filter(Boolean);
    }, [rawRows, headers]);

    const totalSpend = useMemo(() => normalized.reduce((s, r) => s + r.cost, 0), [normalized]);
    const totalConv = useMemo(
        () => normalized.reduce((s, r) => s + r.conversions, 0),
        [normalized]
    );
    const blendedCpa = totalConv > 0 ? totalSpend / totalConv : null;

    const winners = useMemo(() => {
        return normalized
            .filter((r) => r.conversions > 0)
            .sort((a, b) => b.conversions - a.conversions || a.cost_per_conv - b.cost_per_conv);
    }, [normalized]);

    const waste = useMemo(() => {
        return normalized
            .filter((r) => r.conversions === 0 && r.cost >= wasteMinCost)
            .sort((a, b) => b.cost - a.cost);
    }, [normalized, wasteMinCost]);

    // Auto-select all waste rows on new upload.
    useEffect(() => {
        if (!waste.length) return;
        setSelectedNeg(Object.fromEntries(waste.map((w) => [w.location, true])));
    }, [rawRows]);

    const negCount = Object.values(selectedNeg).filter(Boolean).length;

    const handleExportWasteCsv = () => {
        const picked = waste.filter((w) => selectedNeg[w.location]);
        if (!picked.length) {
            toast.error("Select at least one location to export");
            return;
        }
        // Google Ads Editor location-exclusion schema (Campaign, Location, Reach, Bid adj., Status)
        const rows = [["Campaign", "Location", "Reach", "Bid adj.", "Status"]];
        for (const w of picked) {
            rows.push(["", w.location, "People in or interested in your targeted location", "", "Excluded"]);
        }
        const fname = `location_exclusions_${Date.now()}.csv`;
        downloadCsv(fname, toCsv(rows));
        toast.success(`Exported ${picked.length} location exclusions`);
    };

    const clearAll = () => {
        setRawRows([]);
        setHeaders([]);
        setFilename("");
        setSelectedNeg({});
    };

    const hydrateFromAdsRows = (rows, filenameLabel, currency) => {
        const synthHeaders = [
            "Matched location",
            "Added/Excluded",
            "Clicks",
            "Impr.",
            "CTR",
            "Currency code",
            "Cost",
            "Conversions",
            "Cost / conv.",
        ];
        const body = (rows || []).map((r) => [
            // Prefer resolved location name (from geo_target_constant lookup); fall back
            // to legacy country_criterion_id shape for any old cached data.
            r.location || `${r.country_criterion_id ? "Country ID " + r.country_criterion_id + " · " : ""}${r.location_type || ""}` || "Unknown location",
            "None",
            String(r.clicks ?? 0),
            String(r.impressions ?? 0),
            `${(r.ctr ?? 0).toFixed(2)}%`,
            currency || "",
            (r.cost ?? 0).toFixed(2),
            String(r.conversions ?? 0),
            r.cost_per_conv != null ? r.cost_per_conv.toFixed(2) : "0",
        ]);
        setHeaders(synthHeaders);
        setRawRows(body);
        setFilename(filenameLabel);
        setSelectedNeg({});
    };

    const handleFetchFromAds = async (customerId, dateRange, account) => {
        const data = await adsGeographic(customerId, dateRange, account?.name || "");
        const rows = data?.rows || [];
        const name = account?.name ? account.name : `Account ${customerId}`;
        hydrateFromAdsRows(
            rows,
            `${name} · ${dateRange.replace(/_/g, " ").toLowerCase()}`,
            account?.currency || ""
        );
        onAuditSaved?.();
    };

    useEffect(() => {
        if (!preloadedAudit?.audit?.id) return;
        (async () => {
            try {
                const full = await adsAuditGet(preloadedAudit.audit.id);
                hydrateFromAdsRows(full.rows || [], full.label || `Audit ${full.id}`, "");
            } catch (e) {
                toast.error("Could not load audit");
            }
        })();
    }, [preloadedAudit?.key]);

    const handleExportPdf = () => {
        if (!normalized.length) {
            toast.error("Upload a Locations CSV first");
            return;
        }
        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const marginX = 40;
        let y = 48;

        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(10, 10, 10);
        doc.text("Location Performance Audit", marginX, y);
        y += 8;
        doc.setDrawColor(0, 47, 167);
        doc.setLineWidth(3);
        doc.line(marginX, y, marginX + 160, y);
        y += 22;

        // Metadata
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const now = new Date();
        doc.text(`Source: ${filename || "uploaded CSV"}`, marginX, y);
        y += 14;
        doc.text(
            `Generated: ${now.toLocaleString()} · ${normalized.length} locations scanned · waste threshold ${wasteMinCost.toFixed(0)} spend`,
            marginX,
            y
        );
        y += 20;

        // Totals bar with blended CPA
        doc.setFillColor(10, 10, 10);
        doc.rect(marginX, y, 515, 58, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("WINNING LOCATIONS", marginX + 12, y + 14);
        doc.text("WASTE LOCATIONS", marginX + 275, y + 14);
        doc.setFontSize(13);
        doc.text(
            `${winners.length}  ·  ${winners.reduce((s, w) => s + w.conversions, 0).toFixed(1)} conv.`,
            marginX + 12,
            y + 28
        );
        doc.text(
            `${waste.length}  ·  ${waste.reduce((s, w) => s + w.cost, 0).toFixed(2)} at risk`,
            marginX + 275,
            y + 28
        );
        doc.setFontSize(8);
        doc.setTextColor(180, 180, 180);
        doc.text("TOTAL SPEND", marginX + 12, y + 44);
        doc.text("TOTAL CONV.", marginX + 175, y + 44);
        doc.text("BLENDED CPA", marginX + 338, y + 44);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.text(totalSpend.toFixed(2), marginX + 12, y + 54);
        doc.text(totalConv.toFixed(1), marginX + 175, y + 54);
        doc.setTextColor(140, 190, 255);
        doc.text(blendedCpa !== null ? blendedCpa.toFixed(2) : "—", marginX + 338, y + 54);
        y += 74;

        // Winners table
        doc.setTextColor(10, 10, 10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Top Converting Locations", marginX, y);
        y += 8;

        autoTable(doc, {
            startY: y,
            margin: { left: marginX, right: marginX },
            head: [["Location", "Clicks", "Cost", "Conv.", "Conv Rate %", "Cost/Conv"]],
            body: winners.slice(0, 60).map((w) => [
                w.location,
                w.clicks,
                w.cost.toFixed(2),
                w.conversions.toFixed(1),
                w.conv_rate.toFixed(1),
                w.cost_per_conv !== null ? w.cost_per_conv.toFixed(2) : "—",
            ]),
            styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
            headStyles: { fillColor: [0, 47, 167], textColor: 255, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            columnStyles: {
                0: { cellWidth: 220 },
                1: { cellWidth: 45, halign: "right" },
                2: { cellWidth: 60, halign: "right" },
                3: { cellWidth: 45, halign: "right" },
                4: { cellWidth: 65, halign: "right" },
                5: { cellWidth: 65, halign: "right" },
            },
        });

        y = doc.lastAutoTable.finalY + 24;

        if (y > 680) {
            doc.addPage();
            y = 48;
        }

        // Waste table
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Waste Locations · Cost > 0 & Zero Conversions", marginX, y);
        y += 8;

        autoTable(doc, {
            startY: y,
            margin: { left: marginX, right: marginX },
            head: [["Location", "Clicks", "Cost", "CTR %", "Conv.", "Recommendation"]],
            body: waste.slice(0, 100).map((w) => [
                w.location,
                w.clicks,
                w.cost.toFixed(2),
                w.ctr.toFixed(1),
                w.conversions.toFixed(1),
                `Exclude location · ${w.cost.toFixed(2)} unrecovered`,
            ]),
            styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
            headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            columnStyles: {
                0: { cellWidth: 200 },
                1: { cellWidth: 45, halign: "right" },
                2: { cellWidth: 60, halign: "right" },
                3: { cellWidth: 55, halign: "right" },
                4: { cellWidth: 45, halign: "right" },
                5: { cellWidth: "auto", textColor: [180, 40, 40] },
            },
        });

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(140, 140, 140);
            doc.text(
                `SERP-Scout · Location Audit · Page ${i} / ${pageCount}`,
                marginX,
                doc.internal.pageSize.getHeight() - 20
            );
        }

        const fname = `location_audit_${Date.now()}.pdf`;
        doc.save(fname);
        toast.success(`Location audit PDF downloaded · ${winners.length + waste.length} findings`);
    };

    return (
        <div className="flex-1 overflow-y-auto thin-scroll bg-white">
            <div className="max-w-7xl mx-auto p-6 lg:p-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="mono-label">// performance · locations</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-ink mt-1">
                            Location Performance Auditor
                        </h1>
                        <p className="text-sm text-neutral-600 mt-2 max-w-3xl">
                            Upload your Google Ads <span className="font-mono font-bold">Locations</span> report. We surface the cities/regions actually converting versus the ones burning spend, and export a ready-to-import location exclusion list.
                        </p>
                        <div className="mt-3 flex items-start gap-2 text-[11px] text-neutral-500 max-w-3xl bg-neutral-50 border border-neutral-200 p-2">
                            <Warning size={14} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
                            <span>
                                <strong>Note:</strong> Google Ads doesn&apos;t provide search-term × location conversion data in a single export. If you need per-term geo attribution you&apos;ll need custom URL parameters. This audit uses your standard Locations report only.
                            </span>
                        </div>
                    </div>
                    {normalized.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="btn-loc-download-pdf"
                                onClick={handleExportPdf}
                                className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <FilePdf size={13} weight="bold" />
                                Save as PDF
                            </button>
                            <button
                                data-testid="btn-loc-clear"
                                onClick={clearAll}
                                className="brutal-btn border border-neutral-400 bg-white text-neutral-700 font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <Trash size={13} weight="bold" />
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                <AdsImportBar reportKind="geographic" onFetch={handleFetchFromAds} />

                {/* Upload zone */}
                {!normalized.length && (
                    <div
                        data-testid="loc-upload-zone"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add("border-klein", "bg-neutral-50");
                        }}
                        onDragLeave={(e) =>
                            e.currentTarget.classList.remove("border-klein", "bg-neutral-50")
                        }
                        onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove("border-klein", "bg-neutral-50");
                            const file = e.dataTransfer.files?.[0];
                            if (file) handleFile(file);
                        }}
                        className="border-2 border-dashed border-neutral-300 p-12 flex flex-col items-center justify-center cursor-pointer hover:border-ink transition text-center"
                    >
                        <UploadSimple size={44} weight="bold" className="text-neutral-400 mb-3" />
                        <div className="font-heading text-lg font-black text-ink">
                            Drop your Locations report <span className="text-klein">CSV</span> here
                        </div>
                        <div className="text-sm text-neutral-500 mt-1 font-mono">
                            or click to browse · CSV only (not PDF) · Google Ads → Reports → Download → CSV
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="loc-upload-input"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                    </div>
                )}

                {/* Loaded summary bar */}
                {normalized.length > 0 && (
                    <div className="border-2 border-ink bg-ink text-white p-4 flex flex-wrap items-center justify-between gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <FileText size={18} weight="bold" />
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                                    Loaded
                                </div>
                                <div className="font-heading font-black text-sm" data-testid="loc-loaded-filename">
                                    {filename}{" "}
                                    <span className="text-neutral-400 font-mono">
                                        · {normalized.length} locations
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 flex-wrap">
                            <div data-testid="loc-blended-cpa-badge">
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    Blended CPA
                                </div>
                                <div className="font-heading font-black text-sm">
                                    {blendedCpa !== null ? (
                                        <span className="text-sky-300">
                                            {blendedCpa.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-neutral-500">—</span>
                                    )}
                                    <span className="ml-2 text-[10px] font-mono text-neutral-400">
                                        {totalSpend.toFixed(0)} / {totalConv.toFixed(1)}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    Waste threshold
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        data-testid="waste-threshold-slider"
                                        type="range"
                                        min="0"
                                        max="500"
                                        step="10"
                                        value={wasteMinCost}
                                        onChange={(e) =>
                                            setWasteMinCost(parseFloat(e.target.value))
                                        }
                                        className="w-28 accent-klein"
                                    />
                                    <span className="font-heading font-black text-sm min-w-[52px]">
                                        {wasteMinCost.toFixed(0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Two tables */}
                {normalized.length > 0 && (
                    <div className="grid grid-cols-1 gap-8">
                        <LocationTable
                            title="Top Converting Locations"
                            icon={<Trophy size={18} weight="bold" className="text-klein" />}
                            testId="table-loc-winners"
                            rows={winners}
                            emptyText="No locations have registered a conversion yet."
                            columns="win"
                        />

                        <LocationTable
                            title="Waste Locations · Zero Conversions"
                            icon={<Prohibit size={18} weight="bold" className="text-red-600" />}
                            testId="table-loc-waste"
                            rows={waste}
                            emptyText={`No locations exceed the ${wasteMinCost} spend threshold with zero conversions.`}
                            columns="waste"
                            selectable={{
                                selected: selectedNeg,
                                onToggle: (k) =>
                                    setSelectedNeg((p) => ({ ...p, [k]: !p[k] })),
                                onToggleAll: (checked) => {
                                    const next = {};
                                    if (checked)
                                        waste.forEach((w) => (next[w.location] = true));
                                    setSelectedNeg(next);
                                },
                            }}
                            exportBtn={
                                <button
                                    data-testid="btn-export-loc-exclusions"
                                    onClick={handleExportWasteCsv}
                                    className="brutal-btn border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                >
                                    <DownloadSimple size={13} weight="bold" />
                                    Export Location Exclusions · {negCount}
                                </button>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const LocationTable = ({
    title,
    icon,
    testId,
    rows,
    emptyText,
    columns,
    selectable,
    exportBtn,
}) => {
    const allSelected =
        selectable &&
        rows.length > 0 &&
        rows.every((r) => selectable.selected[r.location]);
    return (
        <section data-testid={testId} className="border border-neutral-300 bg-white">
            <div className="flex items-center justify-between p-4 border-b-2 border-ink flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    {icon}
                    <h2 className="font-heading text-lg font-black uppercase tracking-tight text-ink">
                        {title}
                    </h2>
                    <span className="mono-label ml-2">{rows.length}</span>
                </div>
                {exportBtn}
            </div>

            {rows.length === 0 ? (
                <div className="p-8 text-center">
                    <MapPin size={32} weight="bold" className="text-neutral-300 mx-auto mb-2" />
                    <div className="text-sm text-neutral-500">{emptyText}</div>
                </div>
            ) : (
                <div className="overflow-x-auto max-h-[520px] thin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-100 sticky top-0 z-10">
                            <tr className="text-left">
                                {selectable && (
                                    <th className="px-3 py-2 w-8">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={(e) =>
                                                selectable.onToggleAll(e.target.checked)
                                            }
                                            className="accent-klein"
                                            data-testid={`${testId}-toggle-all`}
                                        />
                                    </th>
                                )}
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                    Location
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Clicks
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Cost
                                </th>
                                {columns === "win" ? (
                                    <>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                            Conv.
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right whitespace-nowrap">
                                            Conv Rate
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right whitespace-nowrap">
                                            Cost / Conv.
                                        </th>
                                    </>
                                ) : (
                                    <>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                            CTR
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                            Conv.
                                        </th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.location}
                                    className="border-t border-neutral-200 hover:bg-neutral-50"
                                    data-testid={`${testId}-row`}
                                >
                                    {selectable && (
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={!!selectable.selected[row.location]}
                                                onChange={() =>
                                                    selectable.onToggle(row.location)
                                                }
                                                className="accent-klein"
                                            />
                                        </td>
                                    )}
                                    <td className="px-3 py-2 text-ink font-medium">
                                        {row.location}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.clicks}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.cost.toFixed(2)}
                                    </td>
                                    {columns === "win" ? (
                                        <>
                                            <td className="px-3 py-2 text-right font-mono text-ink">
                                                {row.conversions.toFixed(1)}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-ink">
                                                {row.conv_rate.toFixed(1)}%
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-ink whitespace-nowrap">
                                                {row.cost_per_conv !== null ? (
                                                    <span
                                                        className={
                                                            row.cost_per_conv > 500
                                                                ? "text-red-700"
                                                                : ""
                                                        }
                                                    >
                                                        {row.cost_per_conv.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="text-neutral-400">—</span>
                                                )}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-3 py-2 text-right font-mono text-ink">
                                                {row.ctr.toFixed(1)}%
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-ink">
                                                {row.conversions.toFixed(1)}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
